import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import type { DailyBreakdown } from "../types";
import { ChartSkeleton, ChartEmpty } from "./Charts";
import { useLocale } from "../../../context/LanguageContext";

interface EconomyVelocityChartProps {
  data: DailyBreakdown[];
  loading?: boolean;
}

export function EconomyVelocityChart({
  data,
  loading = false,
}: EconomyVelocityChartProps) {
  const { t } = useLocale();

  if (loading) {
    return <ChartSkeleton title={t("dashboard.economyVelocity")} />;
  }

  if (data.length === 0) {
    return <ChartEmpty title={t("dashboard.economyVelocity")} />;
  }

  const chartData = data.map((d) => {
    const [, m, day] = d.date.split("-");
    return {
      date: `${day}/${m}`,
      earned: d.koinsEarned,
      spent: d.koinsSpent,
    };
  });

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h3 className="mb-4 text-sm font-semibold text-foreground">
        {t("dashboard.economyVelocity")}
      </h3>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--color-border)"
          />
          <XAxis
            dataKey="date"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--color-muted-foreground)" }}
          />
          <YAxis
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--color-muted-foreground)" }}
            tickFormatter={formatCompact}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "var(--color-card)",
              border: "1px solid var(--color-border)",
              borderRadius: "8px",
              color: "var(--color-foreground)",
              fontSize: 12,
            }}
            formatter={(value) => Number(value).toLocaleString()}
          />
          <Legend
            wrapperStyle={{ fontSize: 12, color: "var(--color-foreground)" }}
          />
          <Line
            type="monotone"
            dataKey="earned"
            name={t("dashboard.chartEarned")}
            stroke="#22c55e"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0, fill: "#22c55e" }}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="spent"
            name={t("dashboard.chartSpent")}
            stroke="#ef4444"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0, fill: "#ef4444" }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(value);
}
